import React, { useEffect, useMemo, useState } from 'react';
import { SESSION_TYPES } from '@medflex/utils/constants';
import { trackEvent, TRACKING_ACTIONS } from '@medflex/utils/Tracking';
import { useTranslation, Trans } from 'react-i18next';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import PropTypes from 'prop-types';
import Modal from '@medflex/components/stories/Modal';
import SessionsScheduleForm, {
  Duration,
  Description,
} from '@medflex/components/stories/Sessions/SessionsScheduleForm';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import isSessionMeetingId from '@medflex/utils/isSessionMeetingId';
import commons from '@medflex/commons';
import buildContactData from '@medflex/utils/buildContactData';
import buildModerators from '@medflex/utils/buildModerators';
import Participants from '@medflex/components/stories/Sessions/Participants';
import Link from '@medflex/components/stories/Link';
import getFullDate from '@medflex/utils/getFullDate';
import { addMinutes, format, differenceInMinutes } from 'date-fns';
import createUpgradeUrl from '@medflex/utils/createUpgradeUrl';
import isSessionPMChatId from '@medflex/utils/isSessionPMChatId';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import { dayjs } from '@medflex/shared-utils';
import { SubscriptionStatus } from '@medflex/shared-constants';
import useModal from '../../hooks/useModal';
import useLocationSearchParams from '../../../hooks/useLocationSearchParams';
import useSessionMeetings from '../../hooks/useSessionMeetings';
import useSessionChats from '../../hooks/useSessionChats';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import useSnippets from '../../hooks/useSnippets';
import useUserProfile from '../../hooks/useUserProfile';
import AnonymousSessionSetting from './AnonymousSessionSetting';
import { MODALS } from '../../hooks/useIsModal';
import { UPGRADE_SUBSCRIPTION_PACKAGE_URL } from '../../constants';
import useSubscription from '../../hooks/useSubscription';
import useSessionChat from '../../hooks/useSessionChat';

const MODERATOR = 'moderator';
const ORGANIZER = 'organizer';

const getCurrentDate = () => new Date();

const getModeratorOrganizerForPayload = (userProfile, moderatorUserId) => {
  if (userProfile.id === moderatorUserId) {
    return [{ userId: moderatorUserId, roles: [MODERATOR] }];
  }

  return [
    { userId: moderatorUserId, roles: [MODERATOR] },
    { userId: userProfile.id, roles: [ORGANIZER] },
  ];
};

const SessionsCreateModule = () => {
  const { mode } = useLocationSearchParams();
  const { staff, orgaPhysicians, pairedPatients, pairedPhysicians } = useAddressBookContacts();
  const { close } = useModal();
  const { t } = useTranslation('messenger');
  const { userProfile } = useUserProfile();
  const {
    meetings: meetingSessions,
    load: loadMeetings,
    create,
    errors,
    isLoading,
  } = useSessionMeetings();
  const { chats: chatSessions } = useSessionChats();
  const { sessionId, userId, inChat, copy } = useLocationSearchParams();
  const { chat } = useSessionChat({
    ...(sessionId && isSessionPMChatId(sessionId) && { id: sessionId, load: true }),
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [participants, setParticipants] = useState(false);
  const [selectedModeratorUserId, setSelectedModeratorUserId] = useState(userProfile.id);
  const {
    email: subscriptionEmail,
    plan: subscriptionPlan,
    contractStatus,
    trialEndedAt,
  } = useSubscription();

  const canSendSms =
    contractStatus === SubscriptionStatus.ACTIVE &&
    (!trialEndedAt || dayjs().isAfter(dayjs(trialEndedAt)));

  const moderators = useMemo(() => buildModerators(userProfile, staff), [userProfile, staff]);
  const { snippets } = useSnippets();
  const { physicianUserId } = userProfile.attributes;

  const isSingleMode = mode === 'single';

  useEffect(() => {
    if (sessionId && isSessionMeetingId(sessionId) && meetingSessions.length === 0) loadMeetings();
  }, [sessionId]);

  const handleModeratorChanged = moderatorUserId => setSelectedModeratorUserId(moderatorUserId);

  const contacts = useMemo(() => {
    const filteredContacts = [
      ...orgaPhysicians,
      ...staff.filter(member => member.pairedAt || physicianUserId === member.user.id),
      ...pairedPatients,
      ...pairedPhysicians,
    ].filter(c => !c.mainContactId);
    return buildContactData(filteredContacts, selectedModeratorUserId);
  }, [orgaPhysicians, pairedPatients, pairedPhysicians, selectedModeratorUserId, staff]);

  const {
    defaultParticipants,
    defaultTitle,
    defaultTime,
    defaultDuration,
    defaultDescription,
    defaultAnonymous = AnonymousSessionSetting.get(),
  } = useMemo(() => {
    const sessions = [...meetingSessions, ...chatSessions, ...(chat ? [chat] : [])];
    if (sessionId && sessions.length > 0) {
      const session = sessions.find(({ id }) => id === sessionId);
      if (session) {
        return {
          defaultParticipants: session.participants
            .filter(
              participant =>
                (!isSessionPMChatId(sessionId) && participant.userId !== userProfile.id) ||
                (isSessionPMChatId(sessionId) && isPatientUserId(participant.userId)),
            )
            .map(p => ({ ...p, roles: [] })),
          defaultTitle: isSessionMeetingId(sessionId) ? session.title : '',
          defaultTime: session.scheduledAt && format(session.scheduledAt, 'HH:MM'),
          defaultDuration:
            session.scheduledAt &&
            session.scheduledEndedAt &&
            differenceInMinutes(session.scheduledEndedAt, session.scheduledAt),
          defaultDescription: session.description,
          defaultAnonymous: session.anonymous,
        };
      }
    }

    if (userId && contacts) {
      return {
        defaultParticipants: contacts.filter(c => c.userId === userId),
      };
    }
    return {};
  }, [meetingSessions.length, chatSessions.length, chat, sessionId, userId, contacts]);

  const handleSubmit = ({ date, time, title, duration, description, anonymous }, event) => {
    if (!canSendSms) {
      const hasPhoneParticipants = participants.some(
        ({ referenceType }) => referenceType === 'phone',
      );

      if (hasPhoneParticipants) {
        showAlertToast(
          t('SessionsCreateModule.smsSendError.title'),
          t('SessionsCreateModule.smsSendError.description'),
          {
            toastId: 'sessionCreateFailed',
          },
        );
        setIsSubmitted(false);
        return;
      }
    }
    AnonymousSessionSetting.set(anonymous);
    const scheduledAt = getFullDate({ date, time });
    const filteredParticipants = [
      ...getModeratorOrganizerForPayload(userProfile, selectedModeratorUserId),
      ...participants
        .filter(p => p.userId !== selectedModeratorUserId)
        .map(participant => {
          if (participant.userId) return { userId: participant.userId, roles: participant.roles };
          const { reference, referenceType, inviteParticipant } = participant;
          return {
            firstName: participant.firstName,
            lastName: participant.lastName,
            reference:
              referenceType === 'phone'
                ? commons.phoneNumber.transformToE164(reference).phoneNumberE164
                : reference,
            referenceType,
            inviteParticipant,
          };
        }),
    ];
    const shouldTrackInvitateParticipant = filteredParticipants.some(({ inviteParticipant }) =>
      Boolean(inviteParticipant),
    );

    trackEvent(event, {
      action: inChat ? 'schedule-inchat' : 'schedule',
      actionText: (() => {
        if (anonymous) {
          if (filteredParticipants.length > 2) return 'anonyous schedule1:X';
          return 'anonymous schedule1:1';
        }

        if (filteredParticipants.length > 2) return 'schedule1:X';
        return 'schedule1:1';
      })(),
    });

    if (shouldTrackInvitateParticipant) {
      trackEvent(event, {
        actionText: 'invite non-contact | physician / staff',
      });
    }

    create({
      scheduledAt,
      ...(duration && { scheduledEndedAt: addMinutes(scheduledAt, duration) }),
      ...(description && { description }),
      ...(title && { title }),
      type: SESSION_TYPES.MEETING,
      participants: filteredParticipants,
      anonymous,
    });

    setIsSubmitted(true);
  };

  useEffect(() => {
    const { create: error } = errors;
    if (isSubmitted && !isLoading) {
      if (error) {
        showAlertToast(
          t('global:sessions.SessionsCreateModule.errors.unexpected.title'),
          t('global:sessions.SessionsCreateModule.errors.unexpected.description'),
          {
            toastId: 'sessionCreateFailed',
          },
        );
        setIsSubmitted(false);
        return;
      }
      showSuccessToast(
        t('global:sessions.SessionsCreateModule.success.title'),
        <Link to="/sessions">{t('global:sessions.SessionsCreateModule.success.description')}</Link>,
        {
          toastId: 'sessionCreateSuccess',
        },
      );
      close({
        modal: MODALS.sessionsCreate,
        sessionId,
        userId,
        inChat,
        copy,
        mode,
      });
    }
  }, [errors, isSubmitted, isLoading]);

  const handleClose = () => {
    trackEvent({
      zone: 'SessionsCreate',
      actionText: TRACKING_ACTIONS.CLOSE,
    });
    close({
      modal: MODALS.sessionsCreate,
      sessionId,
      userId,
      inChat,
      copy,
      mode,
    });
  };

  const hasPhoneParticipants = useMemo(() => {
    if (participants.length) {
      return !!participants.find(({ referenceType }) => referenceType && referenceType === 'phone');
    }
    return false;
  }, [participants]);

  const smsUpgradeUrl =
    !canSendSms &&
    createUpgradeUrl({
      baseUrl: UPGRADE_SUBSCRIPTION_PACKAGE_URL,
      plan: subscriptionPlan,
      email: subscriptionEmail,
    });

  return (
    <Modal
      title={
        isSingleMode
          ? t('SessionsCreateModule.title_single')
          : t('SessionsCreateModule.title_group')
      }
      showCloseButton
      onClickCloseButton={handleClose}
    >
      <div data-component="SessionsCreateModule">
        <Participants
          onModeratorChaned={handleModeratorChanged}
          defaultParticipants={defaultParticipants}
          moderators={moderators}
          contacts={contacts}
          maxParticipants={isSingleMode ? 2 : 9}
          onParticipantsChanged={setParticipants}
          canSendSms={canSendSms}
          smsUpgradeUrl={smsUpgradeUrl}
        />
        <section className="mb-4">
          <h3 className="text-xl font-semibold mb-2">{t('SessionsCreateModule.schedule')}</h3>
          <SessionsScheduleForm
            className="bg-slate-100"
            onSubmit={handleSubmit}
            isDisabled={!participants.length || isSubmitted}
            now={getCurrentDate}
            nowButtonHidden={
              !!participants.length &&
              !!participants.find(({ userId: id }) => isSupportPatient({ id }))
            }
            isCopyMode={Boolean(sessionId && copy)}
            defaultTitle={defaultTitle}
            defaultTime={defaultTime}
            defaultAnonymous={defaultAnonymous}
            participantName={
              participants[0] &&
              (participants[0].fullNameWithTitle ||
                getFullNameWithTitle({ id: participants[0].userId, ...participants[0] }))
            }
            maxParticipants={isSingleMode ? 2 : 9}
          >
            {({ errors: err, ...props }) => (
              <>
                <Duration {...props} defaultValue={defaultDuration} error={err && err.duration} />
                {!(hasPhoneParticipants && isSingleMode) && (
                  <Description
                    {...props}
                    defaultValue={defaultDescription}
                    snippets={snippets}
                    error={err && err.description}
                    showWarning={hasPhoneParticipants}
                  />
                )}
              </>
            )}
          </SessionsScheduleForm>
        </section>
        <p className="text-sm text-slate-700 w-full text-center lg:text-right mb-6">
          {t('SessionsCreateModule.notice')}
        </p>
        <p className="text-sm text-slate-700">
          <Trans t={t} i18nKey="SessionsCreateModule.consent_notice">
            <a
              href="https://medflex.de/datenschutz"
              target="_blank"
              className="underline"
              rel="noreferrer"
            >
              Data protection notice
            </a>
          </Trans>
        </p>
      </div>
    </Modal>
  );
};

SessionsCreateModule.propTypes = {
  isSingleMode: PropTypes.bool,
};

SessionsCreateModule.defaultProps = {
  isSingleMode: false,
};

export default SessionsCreateModule;
